import { 
    db, 
    firebase 
} from '../Config/firebase';
import { 
    collection, 
    getDocs, 
    getDoc,
    doc, 
    query, 
    where, 
    orderBy, 
    limit,
    addDoc,
    updateDoc,
    deleteDoc,
    writeBatch,
    setDoc
} from 'firebase/firestore';
import { toast } from 'react-toastify';

// Puzzle interface
export interface ChessPuzzle {
    id: string;
    fen: string;
    moves: string;
    rating: number;
    themes: string[];
    description?: string;
    hint?: string;
    complexity: 'beginner' | 'intermediate' | 'advanced';
    category: 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' | 'checkmate' | 'defense' | 'castle';
    source: 'lichess' | 'custom' | 'local';
    lichessId?: string;
    createdAt?: any;
    createdBy?: string;
}

// Bookmark interface
export interface PuzzleBookmark {
    puzzleId: string;
    userId: string;
    subscriptionId: string;
    createdAt: any;
}

/**
 * Firestore Structure:
 * 
 * Main Puzzle Library (shared across all subscriptions):
 * /serviceProviders/{providerId}/chessPuzzles/{puzzleId}
 * /serviceProviders/{providerId}/chessPuzzles/{puzzleId}/bookmarks/{userId}
 * 
 * Subscription-Specific Puzzles (shortlisted for a specific class):
 * /serviceProviders/{providerId}/Subscriptions/{subscriptionId}/puzzles/{puzzleId}
 * 
 * This allows:
 * - Main library to have 3000+ puzzles (seeded/bulk imported)
 * - Teachers to shortlist 100 puzzles for a specific batch/subscription
 * - Each subscription to have its own puzzle collection for tracking progress
 * 
 * Note: LiveKit integration uses the existing ChessMove interface in ChequeredChess:
 * - type: 'puzzle' - for sharing puzzles
 * - type: 'move' - for sharing moves
 * - type: 'position' - for sharing positions
 */

// Get all puzzles for a service provider
export const getPuzzlesForProvider = async (
    serviceProviderId: string,
    filters?: {
        complexity?: 'beginner' | 'intermediate' | 'advanced' | 'all';
        category?: 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' | 'checkmate' | 'defense' | 'castle' | 'all';
        searchQuery?: string;
        limit?: number;
    }
): Promise<ChessPuzzle[]> => {
    try {
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`);
        
        let q = query(puzzlesRef);
        
        // Apply filters
        if (filters?.complexity && filters.complexity !== 'all') {
            q = query(q, where('complexity', '==', filters.complexity));
        }
        
        if (filters?.category && filters.category !== 'all') {
            q = query(q, where('category', '==', filters.category));
        }
        
        // Order by rating
        q = query(q, orderBy('rating', 'asc'));
        
        if (filters?.limit) {
            q = query(q, limit(filters.limit));
        }
        
        const snapshot = await getDocs(q);
        let puzzles = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChessPuzzle));
        
        // Client-side search filtering
        if (filters?.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            puzzles = puzzles.filter(puzzle => 
                puzzle.description?.toLowerCase().includes(searchLower) ||
                puzzle.themes.some(theme => theme.toLowerCase().includes(searchLower))
            );
        }
        
        return puzzles;
    } catch (error) {
        console.error('Error fetching puzzles:', error);
        toast.error('Failed to load puzzles');
        return [];
    }
};

// Get a single puzzle by ID
export const getPuzzleById = async (
    serviceProviderId: string,
    puzzleId: string
): Promise<ChessPuzzle | null> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`, puzzleId);
        const puzzleDoc = await getDoc(puzzleRef);
        
        if (!puzzleDoc.exists()) {
            console.error('Puzzle not found:', puzzleId);
            return null;
        }
        
        return {
            id: puzzleDoc.id,
            ...puzzleDoc.data()
        } as ChessPuzzle;
    } catch (error) {
        console.error('Error fetching puzzle:', error);
        toast.error('Failed to load puzzle');
        return null;
    }
};

// Get bookmarked puzzles for a user in a subscription
export const getBookmarkedPuzzles = async (
    serviceProviderId: string,
    userId: string
): Promise<string[]> => {
    const startTime = performance.now();
    console.log('⏱️ [BOOKMARKS] Starting getBookmarkedPuzzles', {
        serviceProviderId,
        userId,
        timestamp: new Date().toISOString()
    });
    
    try {
        const step1Time = performance.now();
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`);
        console.log('⏱️ [BOOKMARKS] Step 1: Fetching all puzzles from main library', {
            elapsed: `${(step1Time - startTime).toFixed(2)}ms`
        });
        
        const snapshot = await getDocs(puzzlesRef);
        
        const step2Time = performance.now();
        const puzzleCount = snapshot.docs.length;
        console.log('⏱️ [BOOKMARKS] Step 2: Got all puzzles, checking bookmarks', {
            puzzleCount,
            elapsed: `${(step2Time - step1Time).toFixed(2)}ms`,
            totalElapsed: `${(step2Time - startTime).toFixed(2)}ms`,
            warning: puzzleCount > 100 ? '⚠️ Large puzzle library - this may be slow!' : 'OK'
        });
        
        const bookmarkedIds: string[] = [];
        let bookmarkCheckCount = 0;
        const bookmarkCheckStartTime = performance.now();
        
        // WARNING: This is an N+1 query pattern - could be slow with many puzzles
        // TODO: Optimize by querying bookmarks collection directly instead of checking each puzzle
        for (const puzzleDoc of snapshot.docs) {
            bookmarkCheckCount++;
            
            const bookmarkRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleDoc.id}/bookmarks/${userId}`);
            const bookmarkSnap = await getDoc(bookmarkRef);
            
            if (bookmarkSnap.exists()) {
                bookmarkedIds.push(puzzleDoc.id);
            }
            
            // Log progress every 50 puzzles
            if (bookmarkCheckCount % 50 === 0) {
                const elapsed = performance.now() - bookmarkCheckStartTime;
                const avgTime = elapsed / bookmarkCheckCount;
                const remaining = puzzleCount - bookmarkCheckCount;
                const estimatedRemaining = (remaining * avgTime) / 1000;
                console.log('⏱️ [BOOKMARKS] Progress check', {
                    checked: bookmarkCheckCount,
                    total: puzzleCount,
                    found: bookmarkedIds.length,
                    avgTimePerCheck: `${avgTime.toFixed(2)}ms`,
                    estimatedRemaining: `${estimatedRemaining.toFixed(2)}s`
                });
            }
        }
        
        const endTime = performance.now();
        const totalTime = endTime - startTime;
        const bookmarkCheckTime = endTime - bookmarkCheckStartTime;
        console.log('⏱️ [BOOKMARKS] ✅ Completed getBookmarkedPuzzles', {
            totalPuzzles: puzzleCount,
            bookmarkedCount: bookmarkedIds.length,
            totalTime: `${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`,
            breakdown: {
                fetchPuzzles: `${(step2Time - step1Time).toFixed(2)}ms`,
                checkBookmarks: `${bookmarkCheckTime.toFixed(2)}ms (${(bookmarkCheckTime / 1000).toFixed(2)}s)`,
                avgPerBookmark: `${(bookmarkCheckTime / puzzleCount).toFixed(2)}ms`
            },
            performance: bookmarkCheckTime > 5000 ? '⚠️ SLOW - Consider optimizing bookmark queries' : 'OK'
        });
        
        return bookmarkedIds;
    } catch (error) {
        const endTime = performance.now();
        const totalTime = endTime - startTime;
        console.error('❌ [BOOKMARKS] Error fetching bookmarks:', {
            error,
            totalTime: `${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`,
            serviceProviderId,
            userId
        });
        return [];
    }
};

// Bookmark a puzzle
export const bookmarkPuzzle = async (
    serviceProviderId: string,
    puzzleId: string,
    userId: string,
    subscriptionId: string
): Promise<boolean> => {
    try {
        const bookmarkRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks/${userId}`);
        
        await updateDoc(bookmarkRef, {
            puzzleId,
            userId,
            subscriptionId,
            createdAt: firebase.serverTimestamp()
        }).catch(async () => {
            // If document doesn't exist, create it
            await addDoc(collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks`), {
                puzzleId,
                userId,
                subscriptionId,
                createdAt: firebase.serverTimestamp()
            });
        });
        
        toast.success('Puzzle bookmarked!');
        return true;
    } catch (error) {
        console.error('Error bookmarking puzzle:', error);
        toast.error('Failed to bookmark puzzle');
        return false;
    }
};

// Remove bookmark
export const removeBookmark = async (
    serviceProviderId: string,
    puzzleId: string,
    userId: string
): Promise<boolean> => {
    try {
        const bookmarkRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks/${userId}`);
        await deleteDoc(bookmarkRef);
        
        toast.success('Bookmark removed');
        return true;
    } catch (error) {
        console.error('Error removing bookmark:', error);
        toast.error('Failed to remove bookmark');
        return false;
    }
};

// Add a single puzzle
export const addPuzzle = async (
    serviceProviderId: string,
    puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'>,
    userId: string
): Promise<string | null> => {
    try {
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`);
        
        const docRef = await addDoc(puzzlesRef, {
            ...puzzle,
            createdAt: firebase.serverTimestamp(),
            createdBy: userId
        });
        
        toast.success('Puzzle added successfully!');
        return docRef.id;
    } catch (error) {
        console.error('Error adding puzzle:', error);
        toast.error('Failed to add puzzle');
        return null;
    }
};

// Helper function to generate deterministic puzzle ID
const generatePuzzleId = (puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'>): string => {
    // Create a unique signature from FEN, moves, and rating
    const signature = `${puzzle.fen}|${puzzle.moves}|${puzzle.rating}`;
    // Simple hash function to create a shorter ID
    let hash = 0;
    for (let i = 0; i < signature.length; i++) {
        const char = signature.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    // Convert to base36 and add prefix
    return `puzzle_${Math.abs(hash).toString(36)}`;
};

// Bulk import puzzles (for initial seeding or Lichess import)
export const bulkImportPuzzles = async (
    serviceProviderId: string,
    puzzles: Omit<ChessPuzzle, 'id' | 'createdAt'>[],
    userId: string
): Promise<number> => {
    try {
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`);
        
        let batch = writeBatch(db);
        let count = 0;
        let batchCount = 0;
        
        for (const puzzle of puzzles) {
            // Generate deterministic ID
            const puzzleId = generatePuzzleId(puzzle);
            const docRef = doc(puzzlesRef, puzzleId);
            
            // Use set with merge: true to update existing puzzles
            // This allows replacing old duplicate puzzles with new unique ones
            batch.set(docRef, {
                ...puzzle,
                createdAt: firebase.serverTimestamp(),
                createdBy: userId
            }, { merge: true }); // Changed to merge: true to update existing puzzles
            
            count++;
            batchCount++;
            
            // Firestore batch limit is 500
            if (batchCount >= 500) {
                await batch.commit();
                console.log(`Processed ${count} puzzles...`);
                batch = writeBatch(db);
                batchCount = 0;
            }
        }
        
        // Commit remaining
        if (batchCount > 0) {
            await batch.commit();
        }
        
        console.log(`✅ Bulk import completed: ${count} puzzles processed`);
        return count;
    } catch (error) {
        console.error('Error bulk importing puzzles:', error);
        toast.error('Failed to import puzzles');
        return 0;
    }
};

// Import from Lichess
export const importFromLichess = async (
    serviceProviderId: string,
    lichessId: string,
    userId: string
): Promise<ChessPuzzle | null> => {
    try {
        const response = await fetch(`https://lichess.org/api/puzzle/${lichessId}`);
        
        if (!response.ok) {
            throw new Error('Failed to fetch puzzle from Lichess');
        }
        
        const data = await response.json();
        
        // Convert Lichess puzzle to our format
        const puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'> = {
            fen: data.puzzle.initialPly || data.game.fen,
            moves: data.puzzle.solution.join(' '),
            rating: data.puzzle.rating,
            themes: data.puzzle.themes || [],
            description: `Lichess Puzzle #${data.puzzle.id}`,
            hint: `Themes: ${data.puzzle.themes?.join(', ')}`,
            complexity: data.puzzle.rating < 1500 ? 'beginner' : data.puzzle.rating < 2000 ? 'intermediate' : 'advanced',
            category: determineCategory(data.puzzle.themes),
            source: 'lichess',
            lichessId: lichessId
        };
        
        const puzzleId = await addPuzzle(serviceProviderId, puzzle, userId);
        
        if (puzzleId) {
            return { ...puzzle, id: puzzleId };
        }
        
        return null;
    } catch (error) {
        console.error('Error importing from Lichess:', error);
        toast.error('Failed to import puzzle from Lichess');
        return null;
    }
};

// Helper to determine category from themes
const determineCategory = (themes: string[]): 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' => {
    if (themes.some(t => t.includes('endgame'))) return 'endgame';
    if (themes.some(t => t.includes('opening'))) return 'opening';
    if (themes.some(t => ['middlegame', 'advantage'].includes(t))) return 'middlegame';
    if (themes.some(t => ['strategy', 'positional', 'planning'].includes(t))) return 'strategy';
    if (themes.some(t => ['calculation', 'visualization', 'tactics'].includes(t))) return 'calculation';
    if (themes.some(t => ['pattern', 'recognition', 'motif'].includes(t))) return 'pattern';
    return 'tactics';
};

// Delete a puzzle
export const deletePuzzle = async (
    serviceProviderId: string,
    puzzleId: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
        await deleteDoc(puzzleRef);
        
        toast.success('Puzzle deleted');
        return true;
    } catch (error) {
        console.error('Error deleting puzzle:', error);
        toast.error('Failed to delete puzzle');
        return false;
    }
};

// Delete all puzzles (admin only)
export const deleteAllPuzzles = async (
    serviceProviderId: string
): Promise<number> => {
    try {
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chessPuzzles`);
        const snapshot = await getDocs(puzzlesRef);
        
        let count = 0;
        let batch = writeBatch(db);
        let batchCount = 0;
        
        for (const docSnapshot of snapshot.docs) {
            batch.delete(docSnapshot.ref);
            count++;
            batchCount++;
            
            // Firestore batch limit is 500
            if (batchCount >= 500) {
                await batch.commit();
                batch = writeBatch(db);
                batchCount = 0;
            }
        }
        
        // Commit remaining
        if (batchCount > 0) {
            await batch.commit();
        }
        
        toast.success(`Successfully deleted ${count} puzzles`);
        return count;
    } catch (error) {
        console.error('Error deleting all puzzles:', error);
        toast.error('Failed to delete puzzles');
        return 0;
    }
};

// Update a puzzle
export const updatePuzzle = async (
    serviceProviderId: string,
    puzzleId: string,
    updates: Partial<ChessPuzzle>
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
        await updateDoc(puzzleRef, updates);
        
        toast.success('Puzzle updated');
        return true;
    } catch (error) {
        console.error('Error updating puzzle:', error);
        toast.error('Failed to update puzzle');
        return false;
    }
};

// ========================
// LIVEKIT MEETING INTEGRATION NOTES
// ========================

/**
 * Meeting integration is already handled in ChequeredChess component using sendChessData()
 * 
 * To share a puzzle in a meeting:
 * 
 * 1. Load puzzle from Firestore (use getPuzzlesForProvider or getBookmarkedPuzzles)
 * 2. Use existing sendChessData() function in ChequeredChess:
 * 
 *    sendChessData({
 *        type: 'puzzle',
 *        from: '',
 *        to: '',
 *        san: '',
 *        fen: puzzle.fen,
 *        playerName: participantName,
 *        timestamp: Date.now(),
 *        puzzleId: puzzle.id
 *    });
 * 
 * 3. Students receive via existing dataReceived handler
 * 
 * This integrates seamlessly with the existing LiveKit infrastructure.
 */

// ==================== SUBSCRIPTION PUZZLE MANAGEMENT ====================

/**
 * Get all puzzles for a specific subscription
 */
export const getPuzzlesForSubscription = async (
    serviceProviderId: string,
    subscriptionId: string,
    filters?: {
        complexity?: 'beginner' | 'intermediate' | 'advanced' | 'all';
        category?: 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern' | 'checkmate' | 'defense' | 'castle' | 'all';
        searchQuery?: string;
        limit?: number;
    }
): Promise<ChessPuzzle[]> => {
    const startTime = performance.now();
    console.log('⏱️ [PUZZLE LOAD] Starting getPuzzlesForSubscription', {
        serviceProviderId,
        subscriptionId,
        timestamp: new Date().toISOString()
    });
    
    try {
        const step1Time = performance.now();
        const collectionPath = `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles`;
        console.log('⏱️ [PUZZLE LOAD] Step 1: Creating collection reference', {
            path: collectionPath,
            elapsed: `${(step1Time - startTime).toFixed(2)}ms`
        });
        
        const puzzlesRef = collection(db, collectionPath);
        
        const step2Time = performance.now();
        console.log('⏱️ [PUZZLE LOAD] Step 2: Building query', {
            elapsed: `${(step2Time - step1Time).toFixed(2)}ms`
        });
        
        // Get all puzzles without filters (will be less than 200, so fetch all and filter client-side)
        let q = query(puzzlesRef);
        
        // Order by rating
        q = query(q, orderBy('rating', 'asc'));
        
        // Add limit to prevent loading too many puzzles (default 200 for subscription puzzles)
        const limitValue = filters?.limit || 200;
        q = query(q, limit(limitValue));
        
        const step3Time = performance.now();
        console.log('⏱️ [PUZZLE LOAD] Step 3: Executing Firestore query', {
            limit: limitValue,
            elapsed: `${(step3Time - step2Time).toFixed(2)}ms`,
            totalElapsed: `${(step3Time - startTime).toFixed(2)}ms`
        });
        
        const snapshot = await getDocs(q);
        
        const step4Time = performance.now();
        const queryTime = step4Time - step3Time;
        console.log('⏱️ [PUZZLE LOAD] Step 4: Firestore query completed', {
            docCount: snapshot.docs.length,
            queryTime: `${queryTime.toFixed(2)}ms`,
            totalElapsed: `${(step4Time - startTime).toFixed(2)}ms`
        });
        
        const step5Time = performance.now();
        let puzzles = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChessPuzzle));
        
        const mapTime = step5Time - step4Time;
        console.log('⏱️ [PUZZLE LOAD] Step 5: Mapped documents to puzzles', {
            puzzleCount: puzzles.length,
            mapTime: `${mapTime.toFixed(2)}ms`,
            totalElapsed: `${(step5Time - startTime).toFixed(2)}ms`
        });
        
        // Apply all filters client-side (more efficient for small datasets)
        const step6Time = performance.now();
        if (filters?.complexity && filters.complexity !== 'all') {
            const beforeCount = puzzles.length;
            puzzles = puzzles.filter(puzzle => puzzle.complexity === filters.complexity);
            console.log('⏱️ [PUZZLE LOAD] Applied complexity filter', {
                before: beforeCount,
                after: puzzles.length,
                filter: filters.complexity,
                elapsed: `${(performance.now() - step6Time).toFixed(2)}ms`
            });
        }
        
        if (filters?.category && filters.category !== 'all') {
            const beforeCount = puzzles.length;
            puzzles = puzzles.filter(puzzle => puzzle.category === filters.category);
            console.log('⏱️ [PUZZLE LOAD] Applied category filter', {
                before: beforeCount,
                after: puzzles.length,
                filter: filters.category,
                elapsed: `${(performance.now() - step6Time).toFixed(2)}ms`
            });
        }
        
        // Client-side search filtering
        if (filters?.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            puzzles = puzzles.filter(puzzle => 
                puzzle.description?.toLowerCase().includes(searchLower) ||
                puzzle.themes?.some(theme => theme.toLowerCase().includes(searchLower)) ||
                puzzle.hint?.toLowerCase().includes(searchLower)
            );
        }
        
        const endTime = performance.now();
        const totalTime = endTime - startTime;
        console.log('⏱️ [PUZZLE LOAD] ✅ Completed getPuzzlesForSubscription', {
            totalPuzzles: puzzles.length,
            totalTime: `${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`,
            breakdown: {
                queryTime: `${queryTime.toFixed(2)}ms`,
                mapTime: `${mapTime.toFixed(2)}ms`,
                filterTime: `${(endTime - step5Time).toFixed(2)}ms`
            }
        });
        
        return puzzles;
    } catch (error) {
        const endTime = performance.now();
        const totalTime = endTime - startTime;
        console.error('❌ [PUZZLE LOAD] Error getting subscription puzzles:', {
            error,
            totalTime: `${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`,
            serviceProviderId,
            subscriptionId
        });
        throw error;
    }
};

/**
 * Add a puzzle from main library to a subscription
 * This copies the puzzle to the subscription's puzzle collection
 */
export const addPuzzleToSubscription = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleId: string,
    userId: string
): Promise<string | null> => {
    try {
        // First, get the puzzle from main library
        const mainPuzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
        const mainPuzzleDoc = await getDoc(mainPuzzleRef);
        
        if (!mainPuzzleDoc.exists()) {
            throw new Error('Puzzle not found in main library');
        }
        
        const puzzleData = mainPuzzleDoc.data();
        
        // Check if puzzle already exists in subscription
        const subscriptionPuzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
        const subscriptionPuzzleDoc = await getDoc(subscriptionPuzzleRef);
        
        if (subscriptionPuzzleDoc.exists()) {
            toast.info('Puzzle already exists in this subscription');
            return puzzleId;
        }
        
        // Copy puzzle to subscription collection
        await setDoc(subscriptionPuzzleRef, {
            ...puzzleData,
            addedToSubscriptionAt: firebase.serverTimestamp(),
            addedBy: userId,
            mainPuzzleId: puzzleId // Reference to original puzzle
        });
        
        toast.success('Puzzle added to subscription!');
        return puzzleId;
    } catch (error) {
        console.error('Error adding puzzle to subscription:', error);
        toast.error('Failed to add puzzle to subscription');
        return null;
    }
};

/**
 * Remove a puzzle from a subscription (doesn't delete from main library)
 */
export const removePuzzleFromSubscription = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleId: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
        await deleteDoc(puzzleRef);
        toast.success('Puzzle removed from subscription');
        return true;
    } catch (error) {
        console.error('Error removing puzzle from subscription:', error);
        toast.error('Failed to remove puzzle from subscription');
        return false;
    }
};

/**
 * Mark a puzzle as solved in a subscription
 */
export const markPuzzleAsSolved = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleId: string,
    userId: string,
    userName: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
        await updateDoc(puzzleRef, {
            isSolved: true,
            solvedBy: userId,
            solvedByName: userName,
            solvedAt: firebase.serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error marking puzzle as solved:', error);
        toast.error('Failed to mark puzzle as solved');
        return false;
    }
};

/**
 * Mark a puzzle as taught in a subscription
 */
export const markPuzzleAsTaught = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleId: string,
    userId: string,
    userName: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
        await updateDoc(puzzleRef, {
            isTaught: true,
            taughtBy: userId,
            taughtByName: userName,
            taughtAt: firebase.serverTimestamp()
        });
        toast.success('Puzzle marked as taught');
        return true;
    } catch (error) {
        console.error('Error marking puzzle as taught:', error);
        toast.error('Failed to mark puzzle as taught');
        return false;
    }
};

/**
 * Unmark a puzzle as taught (for re-teaching)
 */
export const unmarkPuzzleAsTaught = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleId: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
        await updateDoc(puzzleRef, {
            isTaught: false,
            taughtBy: null,
            taughtByName: null,
            taughtAt: null
        });
        toast.success('Puzzle unmarked as taught');
        return true;
    } catch (error) {
        console.error('Error unmarking puzzle as taught:', error);
        toast.error('Failed to unmark puzzle as taught');
        return false;
    }
};

/**
 * Check which puzzles from a list are already in the subscription
 */
export const getPuzzlesInSubscription = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleIds: string[]
): Promise<Set<string>> => {
    try {
        const puzzlesRef = collection(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles`);
        const snapshot = await getDocs(puzzlesRef);
        const existingIds = new Set(snapshot.docs.map(doc => doc.id));
        
        // Return intersection of requested IDs and existing IDs
        return new Set(puzzleIds.filter(id => existingIds.has(id)));
    } catch (error) {
        console.error('Error checking puzzles in subscription:', error);
        return new Set();
    }
};

/**
 * Bulk add puzzles to subscription
 */
export const bulkAddPuzzlesToSubscription = async (
    serviceProviderId: string,
    subscriptionId: string,
    puzzleIds: string[],
    userId: string
): Promise<number> => {
    try {
        console.log(`🔄 Starting bulk add: ${puzzleIds.length} puzzles to subscription ${subscriptionId}`);
        
        let batch = writeBatch(db);
        let addedCount = 0;
        let batchCount = 0;
        
        // Get existing puzzles in subscription
        const existingIds = await getPuzzlesInSubscription(serviceProviderId, subscriptionId, puzzleIds);
        console.log(`📊 Found ${existingIds.size} existing puzzles in subscription`);
        
        for (const puzzleId of puzzleIds) {
            // Skip if already exists
            if (existingIds.has(puzzleId)) {
                continue;
            }
            
            // Get puzzle from main library
            const mainPuzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
            const mainPuzzleDoc = await getDoc(mainPuzzleRef);
            
            if (!mainPuzzleDoc.exists()) {
                console.warn(`⚠️ Puzzle ${puzzleId} not found in main library`);
                continue; // Skip if puzzle doesn't exist in main library
            }
            
            const puzzleData = mainPuzzleDoc.data();
            const subscriptionPuzzleRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/puzzles/${puzzleId}`);
            
            batch.set(subscriptionPuzzleRef, {
                ...puzzleData,
                addedToSubscriptionAt: firebase.serverTimestamp(),
                addedBy: userId,
                mainPuzzleId: puzzleId
            });
            
            addedCount++;
            batchCount++;
            
            // Firestore batch limit is 500
            if (batchCount >= 500) {
                console.log(`💾 Committing batch of ${batchCount} puzzles...`);
                await batch.commit();
                batch = writeBatch(db); // Create new batch
                batchCount = 0;
            }
        }
        
        // Commit remaining
        if (batchCount > 0) {
            console.log(`💾 Committing final batch of ${batchCount} puzzles...`);
            await batch.commit();
        }
        
        const skipped = puzzleIds.length - addedCount;
        console.log(`✅ Bulk add complete: ${addedCount} added, ${skipped} skipped`);
        
        if (skipped > 0) {
            toast.success(`Added ${addedCount} puzzles to subscription! (${skipped} skipped - already exist or not found)`);
        } else {
            toast.success(`Added ${addedCount} puzzles to subscription!`);
        }
        
        return addedCount;
    } catch (error) {
        console.error('❌ Error bulk adding puzzles to subscription:', error);
        toast.error(`Failed to add puzzles: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return 0;
    }
};

